import React, { createElement, forwardRef, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { getRadioTestIdByText } from "e2e-tests/testIds";

import { ReactComponent as BackSVG } from "@/assets/img/header/btn.svg";
import Logo from "@/common/Logo";
import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import i18n from "@/localization/i18next";
import { QuestionComponentProps } from "@/types/common";

import useAnswers from "../../utils/useAnswers";

interface CompareResponseProps {
    show: boolean;
    title?: string;
    description?: string;
    icon?: IconComponent;
}
const CompareResponse = forwardRef<HTMLDivElement, CompareResponseProps>(
    ({ show, title, description, icon: Icon }, ref) => (
        <div
            ref={ref}
            className={classNames({
                "grid gap-2 rounded-2xl bg-zing-cool-blue-800 p-4 text-white female:bg-zing-neutral-cool-grey-200 female:text-black":
                    show,
                hidden: !show,
            })}
        >
            <div className="flex items-center gap-2">
                {Icon && <Icon />}
                <span className="text-zing-18-26 font-semibold">{title}</span>
            </div>
            <div className="text-zing-16-22">{description}</div>
        </div>
    )
);

const Radio = ({
    question,
    handleNext,
    handleBack,
    isActive,
}: QuestionComponentProps<QuestionTypes.Radio>) => {
    const [selectedAnsId, setSelectedAnsId] = useState("");
    const [isShowButton, setIsShowButton] = useState(false);
    const answers = useAnswers();
    const { setAnswers } = answers;

    const titleElement = useMemo(() => {
        const title = question?.title;

        if (typeof title === "function") {
            return createElement(title, { answers });
        }

        return title;
    }, [answers, question]);

    const compareAnswer = useMemo(() => {
        const answerMap = question.compareAnswers?.answersMap[selectedAnsId];
        return answerMap ? { ...answerMap, icon: question.compareAnswers?.icon } : null;
    }, [selectedAnsId, question.compareAnswers]);
    const compareAnswerRef = useRef<HTMLDivElement>(null);

    if (!question) return null;

    const scrollToCompareAnswer = (timeout = 0) => {
        setTimeout(() => compareAnswerRef.current?.scrollIntoView({ behavior: "smooth" }), timeout);
    };

    const goToNextQuestion = (ansId: string, isButtonClicked?: boolean) => {
        if (question.compareAnswers?.answersMap[ansId] && !isButtonClicked) {
            setIsShowButton(true);
            scrollToCompareAnswer(compareAnswer ? 0 : 100);
            return;
        }

        handleNext({ [question.id]: ansId });
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: ansId,
        });
        setAnalyticsUserProperties({ [question.id]: ansId });
        sendMetrics(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: ansId,
        });
    };

    const handleContinue = (ansId: string, isButtonClicked?: boolean) => {
        if (["web_cycle", "web_reproductive_stage"].includes(question.id)) {
            setAnswers((prev) => ({ ...prev, [question.id]: ansId }));
        }
        if (
            question.id === QUESTIONS_IDS.EVENT_MOTIVATION ||
            question.id === QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED
        ) {
            setAnswers((prev) => ({ ...prev, [question.id]: ansId }));
        }

        goToNextQuestion(ansId, isButtonClicked);
        setSelectedAnsId(ansId);
    };

    const renderAnswers = () =>
        question.answers?.map(({ svg: SVG, ...ans }) => (
            <div key={ans.id} className="grid">
                <div
                    className={classNames(
                        "flex rounded-2xl p-4 text-zing-18-26 text-white hover:cursor-pointer",
                        {
                            "bg-zing-cool-blue-600 female:bg-zing-warm-tangerine-400 female:text-white":
                                ans.id === selectedAnsId,
                            "pointer-events-none": !isActive && !compareAnswer,
                            "bg-zing-black-200 female:bg-zing-warm-tangerine-100 female:text-black":
                                ans.id !== selectedAnsId,
                            "flex-col items-start gap-1": ans.description,
                            "relative pl-16": SVG,
                        }
                    )}
                    data-zing-test-id={getRadioTestIdByText(ans.text)}
                    onClick={() => handleContinue(ans.id)}
                >
                    {SVG && (
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zing-24-32 leading-6">
                            <SVG className="h-10 w-10" />
                        </div>
                    )}
                    <div className="font-semibold">{ans.text}</div>
                    {ans.description && (
                        <div
                            className={classNames("text-zing-12-18 text-zing-gray-100", {
                                "female:text-white": ans.id === selectedAnsId,
                                "female:text-zing-neutral-cool-grey-600": ans.id !== selectedAnsId,
                            })}
                        >
                            {ans.description}
                        </div>
                    )}
                </div>
            </div>
        )) ?? null;

    return (
        <div
            className={classNames("relative grid h-full grow content-between", {
                "gap-10": !question.withoutHeader,
            })}
        >
            <div className="grid gap-4 pb-6">
                <div className="grid gap-6">
                    {question.withoutHeader && (
                        <div className="relative mt-6 flex h-[44px] items-center">
                            {question.withBackButton && (
                                <button
                                    className="absolute bottom-0 left-0 flex h-7 w-7 items-center justify-center rounded-lg bg-zing-black-200 p-2"
                                    onClick={handleBack}
                                >
                                    <BackSVG className="md:h-4" />
                                </button>
                            )}
                            <Logo className="mx-auto" />
                        </div>
                    )}
                    <div className="grid gap-4">
                        <div
                            className={classNames(
                                "whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black"
                            )}
                        >
                            {titleElement}
                        </div>
                        {question.subtitle && (
                            <div className="whitespace-pre-line text-zing-16-24 text-white female:text-black">
                                {question.subtitle}
                            </div>
                        )}
                    </div>
                    <div className="grid gap-2">{renderAnswers()}</div>
                </div>
                <CompareResponse
                    ref={compareAnswerRef}
                    show={!!compareAnswer}
                    {...(compareAnswer ?? {})}
                />
            </div>
            {isShowButton && (
                <StickyButton
                    onClick={() => handleContinue(selectedAnsId, true)}
                    className={classNames({
                        "pointer-events-none": !isActive && !compareAnswer,
                    })}
                >
                    <span className="normal-case">{i18n.t("continue")}</span>
                </StickyButton>
            )}
        </div>
    );
};

export default Radio;
